/**
 * Organization Model
 * 사업자 조직 도메인 모델
 */

import { ServiceType } from './AdminUser';

// 계정 상태 타입
export type AccountStatus = 'pending' | 'approved' | 'suspended' | 'deactivated';

export interface Organization {
  id: string;
  name: string;
  service_type: ServiceType;
  owner_id: string;
  business_number?: string;
  address?: string;
  phone?: string;
  is_approved: boolean;
  approval_date?: string;
  approved_by?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  // Owner 정보 추가
  owner_name?: string;
  owner_email?: string;
  owner_phone?: string;
  // 할인율
  discount_rate?: number;
  // 계정 상태 (사용 중지 관련)
  account_status?: AccountStatus;
  suspended_at?: string;
  suspended_by?: string;
  suspended_reason?: string;
  reactivated_at?: string;
  reactivated_by?: string;
  // 구독 상태
  subscription_status?: string;
  subscription_plan?: string;
}

export interface OrganizationApprovalRequest {
  organization_id: string;
}

export interface OrganizationRejectionRequest {
  organization_id: string;
  rejection_reason: string;
}

export interface OrganizationUpdateRequest {
  name?: string;
  business_number?: string;
  address?: string;
  phone?: string;
}

// Helper functions
export const isOrganizationPending = (org: Organization): boolean => {
  return !org.is_approved && !org.rejection_reason;
};

export const isOrganizationApproved = (org: Organization): boolean => {
  // 등록 승인 + 구독 정지가 아닌 경우만 정상
  return org.is_approved &&
    org.account_status !== 'suspended' &&
    org.subscription_status !== 'suspended';
};

export const isOrganizationRejected = (org: Organization): boolean => {
  return !org.is_approved && !!org.rejection_reason;
};

export const isOrganizationSuspended = (org: Organization): boolean => {
  // account_status 또는 subscription_status 둘 중 하나라도 suspended면 정지
  return org.account_status === 'suspended' || org.subscription_status === 'suspended';
};

// Aliases for backward compatibility
export const isPending = isOrganizationPending;
export const isApproved = isOrganizationApproved;
export const isRejected = isOrganizationRejected;
export const isSuspended = isOrganizationSuspended;

export const getOrganizationStatusText = (org: Organization): string => {
  // 미승인 상태 우선
  if (isOrganizationPending(org)) return '승인 대기';
  if (isOrganizationRejected(org)) return '승인 거부';
  // 승인 후 → 구독 상태 기준
  if (isOrganizationSuspended(org)) return '정지';
  if (org.subscription_status === 'expired') return '만료';
  if (org.subscription_status === 'trial') return '체험';
  if (org.subscription_status === 'active') return '정상';
  return '정상';
};

export const getOrganizationStatusColor = (org: Organization): string => {
  if (isOrganizationPending(org)) return 'bg-yellow-100 text-yellow-800';
  if (isOrganizationRejected(org)) return 'bg-red-100 text-red-800';
  if (isOrganizationSuspended(org)) return 'bg-orange-100 text-orange-800';
  if (org.subscription_status === 'expired') return 'bg-red-100 text-red-800';
  if (org.subscription_status === 'trial') return 'bg-blue-100 text-blue-800';
  return 'bg-green-100 text-green-800';
};

// 계정 상태별 색상
export const getAccountStatusColor = (status?: AccountStatus): string => {
  switch (status) {
    case 'suspended':
      return 'bg-orange-100 text-orange-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'deactivated':
      return 'bg-gray-100 text-gray-800';
    case 'pending':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

// 계정 상태 텍스트
export const getAccountStatusText = (status?: AccountStatus): string => {
  switch (status) {
    case 'suspended':
      return '사용 중지';
    case 'approved':
      return '정상';
    case 'deactivated':
      return '비활성화';
    case 'pending':
    default:
      return '대기';
  }
};
